import { Table, TableHeader, TableBody, Pagination } from '../../../components/table';
import Search from '../../../components/table/Search';
import ActionIconButton from '../../../components/ui/ActionIconButton';
import StatusBadge from '../../../components/ui/StatusBadge';
import { useState, useEffect, useCallback, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react';
import { toast } from '../../../components/ui/notify';
import Loader from '../../../components/common/Loader';
import Nodata from '../../../components/common/NoData';
import { usePermissions } from '../../../context/PermissionsContext';
import {
  getClassroomAssignmentSubmissions,
  getClassroomAssignmentDetails,
  userAssignmentStatus,
  checkClassroomAssignmentQuizUser
} from '../../../services/organization/classroom/classroomAssignment.service';
import { getSignedMediaUrl } from '../../../services/media.service';
import BitmovinPlayer from '../../../components/common/BitmovinPlayer';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

// Component to display quiz status and action button
function QuizStatusDisplay({ submission, question, checkQuizStatus, handleViewQuizResults }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);
      const quizStatus = await checkQuizStatus(submission.user_id, question.question_id);
      setStatus(quizStatus);
      setLoading(false);
    };
    fetchStatus();
  }, [submission.user_id, question.question_id, checkQuizStatus]);

  if (loading) {
    return (
      <div className="tw-mt-2">
        <StatusBadge tone="neutral">Checking status...</StatusBadge>
      </div>
    );
  }

  if (status && status.exists && status.isCompleted) {
    return (
      <div className="tw-mt-2">
        <button
          className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [[data-theme='dark']_&]:tw-border-[#4895ef] [[data-theme='dark']_&]:tw-text-[#4895ef] [[data-theme='dark']_&:hover]:tw-bg-[#4895ef] [[data-theme='dark']_&:hover]:tw-text-[white] tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] tw-border-[rgb(199_210_254_/_var(--tw-border-opacity,_1))] tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-border-opacity:1] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] [&:hover]:tw-text-[white] [&:hover]:tw-bg-[#4169E1] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-outline-primary ui-button-sm"}
          onClick={(e) => {
            e.stopPropagation();
            handleViewQuizResults(submission, question);
          }}
        >
          <Icon icon="mdi:file-document-outline" className="tw-me-1" />
          View Quiz Results
        </button>
      </div>
    );
  }

  return (
    <div className="tw-mt-2">
      <StatusBadge tone="warning">
        <Icon icon="mdi:clock-outline" className="tw-me-1" />
        Pending
      </StatusBadge>
    </div>
  );
}

function ClassroomAssignmentResponse({decodedClassroomId, decodedAssignmentId}) {
  const { timeZone } = useTimezone();
  const formatAssignmentDateTime = (value) => formatUtcInTimeZone(value, timeZone, 'MMM DD, YYYY, hh:mm A');
  const navigate = useNavigate();
  const { permissions } = usePermissions();

  // State management
  const [submissions, setSubmissions] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState(null);
  const [quizStatuses, setQuizStatuses] = useState({});

  // Search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSubmissions, setTotalSubmissions] = useState(0);

  const resolveResourceUrl = useCallback(async (mediaId, kind) => {
    if (!mediaId) return null;
    return getSignedMediaUrl({
      type: 'resource',
      mediaId,
      kind
    });
  }, []);

  // Fetch assignment details
  const fetchAssignmentDetails = useCallback(async () => {
    if (!decodedClassroomId || !decodedAssignmentId) return;

    try {
      const response = await getClassroomAssignmentDetails({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId
      });

      if (response?.success && response?.data) {
        let attachmentUrl = response.data.attachment_url || null;
        if (!attachmentUrl && response.data.attachment_media_id) {
          attachmentUrl = await resolveResourceUrl(
            response.data.attachment_media_id,
            'assignment_attachment'
          );
        }
        setAssignmentDetails({
          ...response.data,
          attachment_url: attachmentUrl
        });
      } else {
        toast.error(response?.message || 'Failed to fetch assignment details');
      }
    } catch (error) {
      console.error('Error fetching assignment details:', error);
      toast.error('Error fetching assignment details');
    }
  }, [decodedClassroomId, decodedAssignmentId, resolveResourceUrl]);

  // Fetch submissions
  const fetchSubmissions = useCallback(async () => {
    if (!decodedClassroomId || !decodedAssignmentId) return;

    setLoading(true);
    try {
      const response = await getClassroomAssignmentSubmissions({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId,
        page: page,
        limit: limit,
        search: searchTerm
      });


      // API controller returns response.data (body); body may be { success, data: { submissions, pagination } } or { submissions, pagination }
      const body = response?.data ?? response;
      const success = body?.success ?? response?.success;
      const data = body?.data ?? body;
      const submissionsList = data?.submissions ?? [];
      if (success !== false && (submissionsList.length > 0 || data?.pagination != null)) {
        const submissionsWithAnswers = await Promise.all(
          submissionsList.map(async (submission) => {
            const rawQuestions = Array.isArray(submission.questions)
              ? submission.questions
              : (Array.isArray(submission.answers) ? submission.answers : []);

            const questions = await Promise.all(
              rawQuestions.map(async (question) => {
                let questionMediaUrl = question.question_media_url || null;
                if (!questionMediaUrl && question.question_media_id) {
                  questionMediaUrl = await resolveResourceUrl(
                    question.question_media_id,
                    'assignment_question'
                  );
                }

                let submissionMediaUrl = question.submission_media_url || null;
                if (!submissionMediaUrl && question.submission_media_id) {
                  submissionMediaUrl = await resolveResourceUrl(
                    question.submission_media_id,
                    'assignment_submission'
                  );
                }

                return {
                  ...question,
                  question_media_url: questionMediaUrl,
                  submission_media_url: submissionMediaUrl
                };
              })
            );

            return {
              ...submission,
              questions
            };
          })
        );

        setSubmissions(submissionsWithAnswers);

        const pag = data?.pagination;
        if (pag) {
          setTotalPages(pag.totalPages || 1);
          setTotalSubmissions(pag.totalRecords ?? pag.total ?? 0);
        }
      } else if (success === false) {
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Error fetching submissions');
    } finally {
      setLoading(false);
    }
  }, [decodedClassroomId, decodedAssignmentId, page, limit, searchTerm, resolveResourceUrl]);

  // Initialize data
  useEffect(() => {
    if (decodedClassroomId && decodedAssignmentId) {
      fetchAssignmentDetails();
      fetchSubmissions();
    }
  }, [decodedClassroomId, decodedAssignmentId, fetchAssignmentDetails, fetchSubmissions]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleViewQuizResults = (submission, question) => {
    if (!decodedAssignmentId || !submission?.user_id || !question?.question_id) return;

    navigate('/admin/classrooms/assignments/quiz-results', {
      state: {
        assignmentId: decodedAssignmentId,
        quizId: question.question_id,
        userId: submission.user_id,
      },
    });
  };

  // Check quiz status for a user
  const checkQuizStatus = async (userId, quizId) => {
    const key = `${userId}_${quizId}`;
    if (quizStatuses[key]) return quizStatuses[key];

    try {
      const response = await checkClassroomAssignmentQuizUser({
        class_id: decodedClassroomId,
        assessment_id: quizId,
        user_id: userId,
      });
      if (response && response.success && response.data) {
        const data = response.data;
        const status = {
          exists: data.has_access ?? false,
          isCompleted: data.is_completed === 1,
          data,
        };
        setQuizStatuses(prev => ({ ...prev, [key]: status }));
        return status;
      }
    } catch (error) {
      console.error('Error checking quiz status:', error);
    }
    return { exists: false, isCompleted: false };
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  // Toggle row expansion
  const toggleRowExpand = (userId) => {
    if (!permissions.classroom_assignment_response_view_analytics) {
      toast.warning("You don't have permission to view response details");
      return;
    }
    setExpandedRows(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Handle marking answer as wrong
  const handleMarkWrong = async (question, userId) => {
    try {
      const response = await userAssignmentStatus({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId,
        question_id: question.question_id,
        user_id: userId,
        answer_status: 'wrong'
      });

      if (response.success) {
        setSubmissions(prevSubmissions =>
          prevSubmissions.map(submission => {
            if (submission.user_id === userId) {
              return {
                ...submission,
                questions: Array.isArray(submission.questions)
                  ? submission.questions.map(q =>
                      q.question_id === question.question_id
                        ? { ...q, answer_status: 'wrong' }
                        : q
                    )
                  : []
              };
            }
            return submission;
          })
        );
        toast.success(response.message || 'Answer marked as wrong');
      } else {
        toast.error(response.message || 'Failed to update answer status');
      }
    } catch (error) {
      console.error('Error updating answer status:', error);
      toast.error('An error occurred while updating the answer status');
    }
  };

  // Handle marking answer as right
  const handleMarkRight = async (question, userId) => {
    try {
      const response = await userAssignmentStatus({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId,
        question_id: question.question_id,
        user_id: userId,
        answer_status: 'right'
      });

      if (response.success) {
        setSubmissions(prevSubmissions =>
          prevSubmissions.map(submission => {
            if (submission.user_id === userId) {
              return {
                ...submission,
                questions: Array.isArray(submission.questions)
                  ? submission.questions.map(q =>
                      q.question_id === question.question_id
                        ? { ...q, answer_status: 'right' }
                        : q
                    )
                  : []
              };
            }
            return submission;
          })
        );
        toast.success(response.message || 'Answer marked as right');
      } else {
        toast.error(response.message || 'Failed to update answer status');
      }
    } catch (error) {
      console.error('Error updating answer status:', error);
      toast.error('An error occurred while updating the answer status');
    }
  };

  // Render media based on type
  const renderMedia = (url, type, altText = 'Media') => {
    if (!url) return null;

    if (type === 'image') {
      return (
        <img
          src={url}
          alt={altText}
          className="tw-max-w-full tw-mb-4 tw-rounded-xl"
          style={{ maxWidth: '100%', maxHeight: '300px' }}
        />
      );
    }

    if (type === 'audio') {
      return (
        <div className="tw-mb-4">
          <audio controls className="tw-w-full" style={{ minHeight: '50px' }}>
            <source src={url} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    if (type === 'video') {
      return (
        <div className="tw-mb-4">
          <BitmovinPlayer
            src={url}
            title="Submitted video response"
            className="tw-w-full tw-rounded-xl"
            objectFit="contain"
            style={{ maxWidth: '100%', maxHeight: '400px', width: '100%' }}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {/* Assignment Details Section */}
      {assignmentDetails && (
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-6"}>
          <div className="tw-min-w-[0px] ui-column tw-w-full">
            <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"}>
              <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
                  <div>
                    <h4 className="tw-text-[1rem] tw-font-semibold tw-text-[#2c3e50] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] tw-overflow-hidden tw-leading-[1.5rem] ui-card-title tw-mb-2">{assignmentDetails.title || 'Untitled Assignment'}</h4>
                    <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-2">{assignmentDetails.description || 'No description available'}</p>
                  </div>
                  <div className="tw-text-right">
                    <StatusBadge tone={assignmentDetails.is_visible ? 'success' : 'warning'}>
                      {assignmentDetails.is_visible ? 'Visible' : 'Hidden'}
                    </StatusBadge>
                  </div>
                </div>

                <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
                  <div className="tw-min-w-[0px] ui-column md:tw-w-1/3">
                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">Due Date</small>
                    <strong>{assignmentDetails.due_date ? formatAssignmentDateTime(assignmentDetails.due_date) : 'No due date'}</strong>
                  </div>
                  <div className="tw-min-w-[0px] ui-column md:tw-w-1/3">
                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">Assigned Date</small>
                    <strong>{assignmentDetails.assigned_date ? formatAssignmentDateTime(assignmentDetails.assigned_date) : 'N/A'}</strong>
                  </div>
                  <div className="tw-min-w-[0px] ui-column md:tw-w-1/3">
                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">Earning Points</small>
                    <strong>{assignmentDetails.earning_points || 0}</strong>
                  </div>
                </div>

                {assignmentDetails.attachment_url && (
                  <div className="tw-mt-4">
                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-block">Attachment</small>
                    <a href={assignmentDetails.attachment_url} target="_blank" rel="noopener noreferrer" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [[data-theme='dark']_&]:tw-border-[#4895ef] [[data-theme='dark']_&]:tw-text-[#4895ef] [[data-theme='dark']_&:hover]:tw-bg-[#4895ef] [[data-theme='dark']_&:hover]:tw-text-[white] tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] tw-border-[rgb(199_210_254_/_var(--tw-border-opacity,_1))] tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-border-opacity:1] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] [&:hover]:tw-text-[white] [&:hover]:tw-bg-[#4169E1] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-outline-primary ui-button-sm"}>
                      <Icon icon="mdi:file-download-outline" className="tw-me-1" />
                      Download Attachment
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search section */}
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-4"}>
        <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/2 tw-mb-2">
          <Search
            type="text"
            className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input"}
            placeholder="Search User"
            style={{ width: '300px' }}
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* table */}
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-4"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"}>
            {loading ? (
              <div className="tw-flex tw-justify-center tw-my-12">
                <Loader />
              </div>
            ) : submissions.length === 0 ? (
              <div className="tw-flex tw-justify-center tw-my-12">
                <Nodata message="No submissions found" />
              </div>
            ) : (
              <div className="tw-w-full tw-overflow-x-auto tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [-webkit-overflow-scrolling:touch] ui-table-scroll">
                <Table className={"[&_.deleted-accounts-actions_.ui-button-sm]:!tw-w-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-h-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-min-h-[32px] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-p-[0.45rem_1.25rem] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-inline-flex [&_.deleted-accounts-actions_.ui-button-sm]:!tw-items-center [&_.deleted-accounts-actions_.ui-button-sm]:!tw-justify-center [&_.deleted-accounts-actions_.btn-view]:!tw-min-w-[36px] [&_.deleted-accounts-actions_.btn-view]:!tw-p-[0.45rem_0.7rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-restore]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-pr-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-delete]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-pr-[1.35rem] tw-w-full tw-border-collapse tw-text-left tw-text-[0.875rem] tw-leading-[1.25rem] [&_>_thead]:[--tw-bg-opacity:1] [&_>_thead]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-whitespace-nowrap [&_th]:[--tw-border-opacity:1] [&_th]:tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [&_th]:tw-pl-4 [&_th]:tw-pr-4 [&_th]:tw-pt-3 [&_th]:tw-pb-3 [&_th]:tw-text-[0.75rem] [&_th]:tw-leading-[1rem] [&_th]:tw-font-semibold [&_th]:[--tw-text-opacity:1] [&_th]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [&_td]:[--tw-border-opacity:1] [&_td]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_td]:tw-pl-4 [&_td]:tw-pr-4 [&_td]:tw-pt-[0.875rem] [&_td]:tw-pb-[0.875rem] [&_td]:tw-align-middle [&_td]:[--tw-text-opacity:1] [&_td]:tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&_tbody_tr:last-child_td]:tw-border-b-[0px] [&_tbody_tr]:[transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [&_tbody_tr]:[transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [&_tbody_tr]:[transition-duration:150ms] [&_tbody_tr:hover]:[--tw-bg-opacity:1] [&_tbody_tr:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-border-[0px] [&_td]:tw-border-[0px] ui-table ui-table-borderless tw-mb-0"}>
                  <TableHeader>
                    <tr>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '80px' }}>Sl No</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>User Name</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Email</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Total Questions</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Attended</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Remaining</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '100px' }}>Actions</th>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((submission, index) => {
                      const isExpanded = expandedRows[submission.user_id];
                      return (
                        <Fragment key={submission.user_id || index}>
                          <tr className="tw-border-b">
                            <td className="tw-py-4 tw-align-middle">{(page - 1) * limit + index + 1}</td>
                            <td className="tw-py-4 tw-align-middle">{submission.user_name || 'N/A'}</td>
                            <td className="tw-py-4 tw-align-middle">{submission.email || 'N/A'}</td>
                            <td className="tw-py-4 tw-align-middle">
                              <StatusBadge tone="info" className="tw-min-w-[28px] tw-justify-center">{submission.total_questions || 0}</StatusBadge>
                            </td>
                            <td className="tw-py-4 tw-align-middle">
                              <StatusBadge tone="success" className="tw-min-w-[28px] tw-justify-center">{submission.attended_questions || 0}</StatusBadge>
                            </td>
                            <td className="tw-py-4 tw-align-middle">
                              <StatusBadge tone="warning" className="tw-min-w-[28px] tw-justify-center">{submission.remaining_questions || 0}</StatusBadge>
                            </td>
                            <td className="tw-py-4 tw-align-middle">
                              <ActionIconButton
                                icon={isExpanded ? "tabler:chevron-up" : "tabler:chevron-down"}
                                label={isExpanded ? "Hide details" : "Show details"}
                                tone="neutral"
                                title={!permissions.classroom_assignment_response_view_analytics ? "You don't have permission to view response details" : (isExpanded ? 'Hide details' : 'View details')}
                                onClick={() => toggleRowExpand(submission.user_id)}
                                disabled={!permissions.classroom_assignment_response_view_analytics}
                              />
                            </td>
                          </tr>
                          {isExpanded && permissions.classroom_assignment_response_view_analytics && (
                            <tr>
                              <td colSpan={7} className="tw-p-0">
                                <div className="tw-border-t tw-bg-slate-50">
                                  <div className="tw-p-4 tw-border-b">
                                    <h5 className="tw-mb-0 tw-font-light">Questions & Answers</h5>
                                  </div>
                                  <div className="tw-p-6">
                                    {Array.isArray(submission.questions) && submission.questions.length > 0 ? (
                                      submission.questions.map((question, qIndex) => (
                                        <div key={qIndex} className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-mb-6"}>
                                          <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                                            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                                              <h6 className="!tw-text-[var(--secondary-color)] tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] ui-card-subtitle tw-text-slate-500 tw-mb-0">
                                                <span className="tw-font-bold">Question {qIndex + 1}</span>
                                              </h6>
                                              {/* Only show right/wrong buttons for non-quiz responses */}
                                              {question.response_type !== 'quiz_response' && (
                                                <div className="tw-flex tw-gap-2">
                                                  <button
                                                    className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm   ${question.answer_status === "wrong" ? "[--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] ui-button-danger" : "[--tw-border-opacity:1] tw-border-[rgb(254_205_211_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(190_18_60_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(255_241_242_/_var(--tw-bg-opacity,_1))] ui-button-outline-danger"}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (question.answer_status !== 'wrong') {
                                                        handleMarkWrong(question, submission.user_id);
                                                      }
                                                    }}
                                                    disabled={question.answer_status === 'wrong'}
                                                  >
                                                    Wrong
                                                  </button>
                                                  <button
                                                    className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm   ${question.answer_status === "right" ? "[--tw-bg-opacity:1] tw-bg-[rgb(5_150_105_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(4_120_87_/_var(--tw-bg-opacity,_1))] ui-button-success" : "[--tw-border-opacity:1] tw-border-[rgb(167_243_208_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(4_120_87_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(236_253_245_/_var(--tw-bg-opacity,_1))] ui-button-outline-success"}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (question.answer_status !== 'right') {
                                                        handleMarkRight(question, submission.user_id);
                                                      }
                                                    }}
                                                    disabled={question.answer_status === 'right'}
                                                  >
                                                    Right
                                                  </button>
                                                </div>
                                              )}
                                            </div>

                                            {/* Question Content */}
                                            <div className="tw-mb-4 tw-p-4 tw-bg-slate-50 tw-rounded-xl">
                                              <h6 className="tw-mb-2 tw-font-light">Question:</h6>
                                              {question.question_text && (
                                                <p className="tw-mb-2" style={{ whiteSpace: 'pre-wrap' }}>{question.question_text}</p>
                                              )}

                                              {/* Question Media */}
                                              {question.question_media_url && (
                                                <div className="tw-mt-2">
                                                  <h6 className="tw-mb-2 tw-font-light">Question Media:</h6>
                                                  {renderMedia(
                                                    question.question_media_url,
                                                    question.question_type === 'image'
                                                      ? 'image'
                                                      : question.question_type === 'audio'
                                                        ? 'audio'
                                                        : 'video'
                                                  )}
                                                </div>
                                              )}

                                              {/* Video Progress */}
                                              {question.question_type === 'video' && question.video_progress !== undefined && (
                                                <div className="tw-mt-4">
                                                  <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                                                    <small className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Video Progress</small>
                                                    <small className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">{question.video_progress}%</small>
                                                  </div>
                                                  <div className="tw-bg-[rgb(241_245_249_/_var(--tw-bg-opacity,_1))] tw-rounded-full tw-overflow-hidden tw-flex tw-h-2 [--tw-bg-opacity:1] ui-progress" style={{ height: '6px' }}>
                                                    <div
                                                      className="tw-h-full tw-rounded-full [transition:width_0.3s_ease] tw-flex tw-items-center tw-justify-center [--tw-bg-opacity:1] tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [transition-property:width] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:300ms] ui-progress-bar tw-bg-emerald-600"
                                                      role="progressbar"
                                                      style={{ width: `${question.video_progress}%` }}
                                                      aria-valuenow={question.video_progress}
                                                      aria-valuemin="0"
                                                      aria-valuemax="100"
                                                    ></div>
                                                  </div>
                                                </div>
                                              )}

                                              {/* Quiz Response - check status and show button or pending */}
                                              {question.response_type === 'quiz_response' && (
                                                <QuizStatusDisplay
                                                  submission={submission}
                                                  question={question}
                                                  checkQuizStatus={checkQuizStatus}
                                                  handleViewQuizResults={handleViewQuizResults}
                                                />
                                              )}
                                            </div>

                                            {/* User's Answer */}
                                            <div className="tw-mb-4 tw-p-4 tw-bg-slate-50 tw-rounded-xl">
                                              <h6 className="tw-mb-2">
                                                <StatusBadge tone="info" className="tw-me-2">Trainee Answer</StatusBadge>
                                              </h6>

                                              {/* Text Response */}
                                              {question.response_type === 'text_response' && question.answer_text && (
                                                <div className="tw-p-2 tw-bg-white tw-rounded-xl">
                                                  <p className="tw-mb-0">{question.answer_text}</p>
                                                </div>
                                              )}

                                              {/* Image Response */}
                                              {question.response_type === 'image_response' && question.submission_media_url && (
                                                <div>
                                                  {renderMedia(question.submission_media_url, 'image', 'Trainee\'s image response')}
                                                </div>
                                              )}

                                              {/* Audio Response */}
                                              {question.response_type === 'audio_response' && question.submission_media_url && (
                                                <div>
                                                  <h6 className="tw-mb-2">Audio Response:</h6>
                                                  {renderMedia(question.submission_media_url, 'audio')}
                                                </div>
                                              )}

                                              {/* Video Response */}
                                              {question.response_type === 'video_response' && question.submission_media_url && (
                                                <div>
                                                  <h6 className="tw-mb-2">Video Response:</h6>
                                                  {renderMedia(question.submission_media_url, 'video')}
                                                </div>
                                              )}

                                              {/* No answer provided (exclude quiz type which is handled separately) */}
                                              {question.response_type !== 'quiz_response' && !question.answer_text && !question.submission_media_url && (
                                                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500">No answer provided</div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500">No question details available</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* record per page and pagination */}
      {!loading && submissions.length > 0 && (
        <Pagination page={page} pageSize={limit} totalRecords={totalSubmissions} totalPages={totalPages} onPageChange={handlePageChange} onPageSizeChange={(size) => handleLimitChange({ target: { value: size } })} pageSizeOptions={[5, 10, 25, 50, 100]} />
      )}
    </>
  )
}

export default ClassroomAssignmentResponse
